import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Login.css';

const ForgotPassword = () => {
  const [formData, setFormData] = useState({
    credential: '',
    activationCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const { credential, activationCode, newPassword, confirmPassword } = formData;

    if (!credential.trim()) {
      setError('Email or username is required');
      setLoading(false);
      return;
    }
    if (!/^\d{6}$/.test(String(activationCode).trim())) {
      setError('Activation code must be 6 digits');
      setLoading(false);
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.resetPassword({
        credential: credential.trim(),
        activationCode: String(activationCode).trim(),
        newPassword,
        confirmPassword
      });
      if (response.data.success) {
        setSuccess(response.data.message || 'Password reset successful');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      } else {
        setError(response.data.message || 'Password reset failed');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.data?.errors && err.response.data.errors.map((x) => x.msg).join(', ')) ||
        'Password reset failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page premium-page">
      <div className="auth-page__bg" />

      <div className="auth-card">
        <button
          type="button"
          className="auth-card__close"
          onClick={() => navigate('/')}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="auth-card__header">
          <Link to="/" className="auth-card__logo">
            <svg viewBox="0 0 24 24" fill="none" width="40" height="40">
              <path d="M3 17L9 11L13 15L21 7" stroke="url(#auth-logo)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17 7H21V11" stroke="url(#auth-logo)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <defs>
                <linearGradient id="auth-logo" x1="3" y1="7" x2="21" y2="17" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#60a5fa"/>
                  <stop offset="1" stopColor="#1d4ed8"/>
                </linearGradient>
              </defs>
            </svg>
          </Link>
          <h1 className="auth-card__title">Reset Password</h1>
          <p className="auth-card__subtitle">Use an activation code to set a new password</p>
        </div>

        {error && (
          <div className="auth-card__error">
            <span>⚠️ {error}</span>
          </div>
        )}

        {success && (
          <div className="auth-card__error" style={{ background: 'rgba(34, 197, 94, 0.12)', color: '#15803d', borderColor: 'rgba(34, 197, 94, 0.35)' }}>
            <span>✓ {success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form__field">
            <label htmlFor="credential">Email or Username</label>
            <input
              type="text"
              id="credential"
              name="credential"
              value={formData.credential}
              onChange={handleChange}
              placeholder="your@email.com or username"
              required
              autoComplete="username"
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor="activationCode">Activation Code</label>
            <input
              type="text"
              id="activationCode"
              name="activationCode"
              value={formData.activationCode}
              onChange={handleChange}
              placeholder="6-digit code"
              maxLength={6}
              inputMode="numeric"
              required
              autoComplete="off"
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="At least 6 characters"
              required
              autoComplete="new-password"
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter new password"
              required
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            className={`auth-form__submit ${loading ? 'auth-form__submit--loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="auth-form__spinner"></span>
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="auth-card__footer">
          <p>Remember your password? <Link to="/login">Sign In</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
